import { useCallback, useEffect, useMemo } from 'react'

import { CursorState } from '@editor/config/constants/cursor'
import { DrawingParameterRectangle, DrawingType, PolygonSubtype } from '@editor/interfaces/drawing'
import { EditorConfig } from '@editor/interfaces/editor'
import { BlueprintEditorTools } from '@editor/pages/projects/blueprint-editor/definitions'
import { removeDrawings } from '@editor/pages/projects/blueprint-editor/store/drawings'
import { isDrawingOfType } from '@editor/services/Drawing'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setCursor, setWorkingElementId } from '@editor/store/tool'
import { useSelector } from 'react-redux'

import useUndo from './useUndo'

export default function useEditor(): EditorConfig {
  // Store
  const dispatch = useAppDispatch()
  const selectedTool = useSelector((state: RootState) => state.blueprint.editor.selectedTool)
  const prevSelectedTool = useSelector((state: RootState) => state.blueprint.editor.prevSelectedTool)
  const workingElementId = useSelector((state: RootState) => state.toolUtil.core.workingElementId)
  const drawings = useSelector((state: RootState) => state.blueprint.drawings.document.drawings)

  // Derived state
  const isToolSelected = useMemo(() => selectedTool === BlueprintEditorTools.Rectangle, [selectedTool])
  const isPreviousTool = useMemo(() => prevSelectedTool === BlueprintEditorTools.Rectangle, [prevSelectedTool])
  const hasWorking = useMemo(
    () =>
      drawings.some(
        (d) =>
          isDrawingOfType<DrawingParameterRectangle>(d, DrawingType.Polygon) &&
          d.drawing_parameters.subtype === PolygonSubtype.Rectangle &&
          d.drawing_parameters.uuid === workingElementId,
      ),
    [drawings, workingElementId],
  )

  // Hooks
  const { canUndo } = useUndo()

  /**
   * When user changes tools (in or out of Rectangle tool), update cursor.
   */
  useEffect(() => {
    if (isToolSelected) {
      dispatch(setCursor(CursorState.CROSSHAIR))
    } else if (isPreviousTool && selectedTool !== BlueprintEditorTools.Focus) {
      dispatch(setCursor(CursorState.DEFAULT))
      if (hasWorking && workingElementId) {
        dispatch(removeDrawings([workingElementId]))
        dispatch(setWorkingElementId(undefined))
      }
    }
  }, [isToolSelected, selectedTool, isPreviousTool, hasWorking, workingElementId, dispatch])

  return {
    buttons: {
      undo: {
        isShown: useCallback(() => isToolSelected, [isToolSelected]),
        isDisabled: useCallback(() => !canUndo, [canUndo]),
      },
    },
  }
}
